import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Trophy, Wallet, Zap, Loader2, Mail, Lock, User } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

const LOGO = "/paras-logo.png";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'signup'
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "customer" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "forgot") {
      if (!form.email) return toast.error("Enter your email");
      setBusy(true);
      try {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(data.message || "If an account exists, a reset link has been sent.");
        setMode("login");
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally { setBusy(false); }
      return;
    }
    if (!form.email || !form.password) return toast.error("Email and password are required");
    if (mode === "signup" && form.password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const payload = mode === "signup"
        ? { email: form.email, password: form.password, name: form.name, role: form.role }
        : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      setUser(data.user);
      toast.success(mode === "signup" ? "Account created" : "Welcome back");
      navigate("/dashboard", { replace: true, state: { user: data.user } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-slate-700 bg-slate-900 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-emerald-500";

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-100 lg:grid lg:grid-cols-2">
      {/* LEFT — brand panel */}
      <div className="paras-grid-bg relative hidden overflow-hidden border-r border-white/10 bg-gradient-to-br from-emerald-500/10 via-[#0b111d] to-cyan-500/10 lg:flex lg:flex-col lg:justify-between lg:p-12">
        <a href="/" className="flex items-center gap-3" data-testid="login-brand">
          <img src={LOGO} alt="Paras Cashback" className="h-11 w-11 rounded-xl object-contain" />
          <div className="leading-none"><p className="font-heading text-lg font-extrabold">Paras Cashback™</p><p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">Shop · Token · Cashback</p></div>
        </a>
        <div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight">Smart shopping,<br /><span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">smarter cashback.</span></h2>
          <div className="mt-8 space-y-4">
            {[[Zap, "Instant ₹20/₹50/₹100 cashback"], [Trophy, "FREE Lucky Draw entries"], [Wallet, "Rewards paid straight to your wallet"], [ShieldCheck, "Secure, ledger-backed transactions"]].map(([I, t]) => (
              <div key={t} className="flex items-center gap-3 text-sm text-slate-300"><span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-500/15 text-emerald-400"><I className="h-4 w-4" /></span>{t}</div>
            ))}
          </div>
        </div>
        <p className="font-mono text-[11px] text-slate-500">© {new Date().getFullYear()} Paras Cashback™</p>
      </div>

      {/* RIGHT — sign-in */}
      <div className="flex min-h-screen flex-col justify-center px-6 py-10 sm:px-10">
        <div className="mx-auto w-full max-w-md">
          <button onClick={() => navigate("/")} className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-home">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </button>

          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src={LOGO} alt="Paras Cashback" className="h-10 w-10 rounded-xl object-contain" />
            <p className="font-heading text-lg font-extrabold">Paras Cashback™</p>
          </div>

          <div className="ledger-card rounded-3xl border border-white/10 p-8 shadow-2xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 font-mono text-xs uppercase tracking-widest text-emerald-400">
              <ShieldCheck className="h-3.5 w-3.5" /> Secure sign-in
            </div>

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-1 rounded-full bg-slate-900 p-1" data-testid="auth-tabs">
              <button data-testid="tab-login" onClick={() => setMode("login")} className={`rounded-full py-2 text-sm font-semibold transition ${mode === "login" ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`}>Login</button>
              <button data-testid="tab-signup" onClick={() => setMode("signup")} className={`rounded-full py-2 text-sm font-semibold transition ${mode === "signup" ? "bg-emerald-500 text-slate-950" : "text-slate-400"}`}>Sign up</button>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="auth-form">
              {mode === "signup" && (
                <>
                  <div className="relative"><User className="absolute left-3 top-3 h-4 w-4 text-slate-500" /><input data-testid="auth-name" placeholder="Full name" value={form.name} onChange={set("name")} className={inputCls} /></div>
                  <div className="grid grid-cols-2 gap-2" data-testid="auth-role">
                    {[["customer", "I'm a Customer"], ["merchant", "I'm a Merchant"]].map(([r, l]) => (
                      <button key={r} type="button" data-testid={`role-${r}`} onClick={() => setForm({ ...form, role: r })}
                        className={`rounded-lg border px-3 py-2.5 text-sm font-semibold transition ${form.role === r ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-slate-700 text-slate-400"}`}>{l}</button>
                    ))}
                  </div>
                </>
              )}
              <div className="relative"><Mail className="absolute left-3 top-3 h-4 w-4 text-slate-500" /><input data-testid="auth-email" type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} /></div>
              {mode !== "forgot" && (
                <div className="relative"><Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" /><input data-testid="auth-password" type="password" placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"} value={form.password} onChange={set("password")} className={inputCls} /></div>
              )}
              {mode === "login" && (
                <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")} className="block w-full text-right text-xs text-emerald-400 hover:underline">Forgot password?</button>
              )}
              {mode === "forgot" && (
                <p className="text-xs text-slate-500">Enter your account email and we'll send you a reset link.</p>
              )}
              <button data-testid="auth-submit" type="submit" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Login"}
              </button>
              {mode === "forgot" && (
                <button type="button" data-testid="back-to-login" onClick={() => setMode("login")} className="block w-full text-center text-xs text-slate-400 hover:text-white">← Back to login</button>
              )}
            </form>

            <div className="my-6 flex items-center gap-3 text-xs text-slate-600"><div className="h-px flex-1 bg-slate-800" /> OR <div className="h-px flex-1 bg-slate-800" /></div>

            <button data-testid="google-signin-button" onClick={googleLogin} className="flex w-full items-center justify-center gap-3 rounded-full bg-white py-3.5 font-semibold text-slate-900 transition hover:bg-slate-100">
              <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
              Continue with Google
            </button>
          </div>
          <p className="mt-6 text-center text-xs text-slate-500">By continuing you agree to our <a href="/terms" className="text-emerald-400 hover:underline">Terms</a> &amp; <a href="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</a>.</p>
        </div>
      </div>
    </div>
  );
}
